// Reagrupa la estructura `inspeccion` (por sector → actividad → celdas por
// ascensor) a `equipos` (por ascensor → sector → actividad), que es lo que
// consume la sección "bloque por ascensor" del informe de mantención.
//
// Es capa de PRESENTACIÓN (transform puro): la extracción de datos la hace
// `mapData.js` (consola-unificar-hallazgos). Contrato:
// thoughts/coordination/contrato-datos-informe-bloque-ascensor.md
//
// Regla señal/ruido: solo las celdas en FALLA exponen hallazgos; las conformes
// muestran únicamente "qué se hizo" (sin el ruido histórico "Estado normal").

const GRAVE = new Set(['ALTA', 'CRITICA']);
const SEVERIDADES = ['BAJA', 'MEDIA', 'ALTA', 'CRITICA'];
const NOISE_OBS = new Set(['', 'Estado normal']);
const NOISE_SUG = new Set(['', 'Sin sugerencias']);

function normalizeSeveridad(sev) {
  const s = (sev || 'MEDIA').toString().toUpperCase();
  return SEVERIDADES.includes(s) ? s : 'MEDIA';
}

// Agrupa los hallazgos de una celda por TIPO: incidencias (con severidad) y
// sugerencias (texto). Soporta el shape nuevo multi-hallazgo (`cell.hallazgos[]`
// con `tipo`) y el legacy (`cell.obs`/`cell.detalle`/`cell.sug`).
//
// - Incidencias implican un problema → solo se derivan del fallback legacy en
//   FALLA. Las sugerencias se muestran en cualquier estado (también conforme).
// - Defensivo: descarta hallazgos INACTIVA (un hallazgo eliminado del checklist
//   no debe aparecer en el informe).
function hallazgosDeCelda(cell, esFalla) {
  const incidencias = [];
  const sugerencias = [];

  if (Array.isArray(cell.hallazgos) && cell.hallazgos.length > 0) {
    for (const h of cell.hallazgos) {
      if (h.estado && h.estado !== 'ACTIVA') continue;
      const desc = (h.descripcion || h.detalle || '').trim();
      if (!desc) continue;
      // Fotos del hallazgo (backend: imagenes:[{url,orden}]). Tope defensivo de 4.
      const imagenes = Array.isArray(h.imagenes) ? h.imagenes.slice(0, 4) : [];
      if (h.tipo === 'SUGERENCIA') {
        sugerencias.push({ descripcion: desc, imagenes });
      } else {
        incidencias.push({
          descripcion: desc,
          severidad: normalizeSeveridad(h.severidad),
          imagenes,
        });
      }
    }
    return { incidencias, sugerencias };
  }

  // Fallback legacy: solo en FALLA (el texto de una celda conforme es ruido de
  // conformidad). `cell.detalle` = texto combinado (unificar-hallazgos);
  // `cell.obs` = campo legacy/mock. Se prioriza `detalle`.
  if (!esFalla) return { incidencias, sugerencias };
  const texto = (cell.detalle || cell.obs || '').trim();
  const sug = (cell.sug || '').trim();
  const descripcion = NOISE_OBS.has(texto) ? '' : texto;
  const sugerencia = NOISE_SUG.has(sug) ? '' : sug;
  // Legacy no tiene fotos por hallazgo → imagenes vacío (shape consistente).
  if (descripcion)
    incidencias.push({ descripcion, severidad: normalizeSeveridad(cell.severidad), imagenes: [] });
  if (sugerencia) sugerencias.push({ descripcion: sugerencia, imagenes: [] });
  return { incidencias, sugerencias };
}

export function buildEquiposFromInspeccion(inspeccion) {
  const byEquipo = new Map(); // Map preserva el orden de aparición de ascensores

  for (const sec of inspeccion || []) {
    for (const item of sec?.items || []) {
      for (const [asc, cell] of Object.entries(item?.cells || {})) {
        if (!byEquipo.has(asc)) {
          byEquipo.set(asc, { key: asc, nombre: asc, sectores: new Map() });
        }
        const equipo = byEquipo.get(asc);
        if (!equipo.sectores.has(sec.sector)) equipo.sectores.set(sec.sector, []);
        const esFalla = cell?.estado === 'FALLA';
        const { incidencias, sugerencias } = hallazgosDeCelda(cell, esFalla);
        equipo.sectores.get(sec.sector).push({
          act: item.act,
          estado: esFalla ? 'falla' : 'conforme',
          incidencias,
          sugerencias,
        });
      }
    }
  }

  return Array.from(byEquipo.values()).map((eq) => {
    const sectores = Array.from(eq.sectores.entries()).map(([sector, items]) => ({
      sector,
      items,
    }));
    let actividades = 0;
    let fallas = 0;
    let grave = false;
    for (const s of sectores) {
      for (const it of s.items) {
        actividades += 1;
        if (it.estado === 'falla') {
          fallas += 1;
          if (it.incidencias.some((h) => GRAVE.has(h.severidad))) grave = true;
        }
      }
    }
    const estado_global = fallas === 0 ? 'conforme' : grave ? 'critico' : 'observaciones';
    return {
      key: eq.key,
      nombre: eq.nombre,
      estado_global,
      conteos: { actividades, conformes: actividades - fallas, fallas },
      sectores,
    };
  });
}

export default buildEquiposFromInspeccion;
